import React, { useEffect, useRef } from 'react';
import { useChat } from '../../../hooks/api/useChat';
import { Animated, Platform, View } from 'react-native';
import { useProfilesTabNavigation } from '../../../utils/appNavigationNames';
import { getIntl } from '../../../utils/intl';
import { colors } from '../../../utils/theme';
import { PRScrollView } from '../Scroll';
import PressableDynamicOpacity from '../../v2BaseComponents/PressableDynamicOpacity';
import { mainContentPadding } from '../../v2BaseComponents/MainContentContainer';
import Stack from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import getDateString from '../../../utils/getDateString';

const intl = getIntl('DesktopChatHistory', {
    chats: 'Previous Chats',
});

export type DesktopChatHistoryOwnProps = {
    medicalProfileId: string;
    onClose: () => void;
};

export type DesktopChatHistoryProps = DesktopChatHistoryOwnProps;

const DesktopChatHistory: React.FC<DesktopChatHistoryProps> = ({ medicalProfileId, onClose }) => {
    const { navigate } = useProfilesTabNavigation();
    const { chats } = useChat({
        medicalProfileId: medicalProfileId,
    });
    const hasChats = (chats?.items.length || 0) > 0;
    const appearanceAnimation = useRef<Animated.Value>(new Animated.Value(hasChats ? 1 : 0));
    useEffect(() => {
        if (hasChats) {
            Animated.timing(appearanceAnimation.current, {
                toValue: 1,
                duration: 200,
                useNativeDriver: Platform.OS === 'web',
            }).start();
        }
    }, [hasChats]);

    return (
        <Animated.View
            style={{
                opacity: appearanceAnimation.current,
                width: appearanceAnimation.current.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, 300],
                }),
                overflow: 'hidden',
                borderLeftWidth: 1,
                borderLeftColor: colors.Dividers,
                alignSelf: 'stretch',
            }}
            testID="desktop-chat-history"
        >
            <View
                style={{
                    flex: 1,
                    alignSelf: 'stretch',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    width: 300,
                }}
            >
                <PRScrollView style={{ flex: 1 }}>
                    <Stack vertical space={4} style={{ flex: 1 }}>
                        <Text
                            variant="bold-h6"
                            style={{ padding: mainContentPadding, paddingBottom: 0 }}
                            color="Primary-Reg"
                        >
                            {intl('chats')}
                        </Text>
                        {(chats?.items || []).map((chat) => (
                            <PressableDynamicOpacity
                                style={{
                                    alignSelf: 'stretch',
                                    paddingLeft: mainContentPadding,
                                    paddingRight: mainContentPadding,
                                }}
                                onPress={() => {
                                    onClose();
                                    navigate('chat', { profileId: medicalProfileId, defaultChatId: chat.chatId });
                                }}
                                key={chat.chatId}
                            >
                                <Text variant="bold-body-1">{chat.title || ''}</Text>
                                <Text variant="caption-1">{getDateString(chat.createdDateTime, 'date-time')}</Text>
                            </PressableDynamicOpacity>
                        ))}
                    </Stack>
                </PRScrollView>
            </View>
        </Animated.View>
    );
};

export default React.memo(DesktopChatHistory);
